import VideoStream from "app/lib/bmmr-asset-js/video-stream";
import Caption from "./caption";
import ComscoreMetadata from "./comscore-metadata";
import PosterSize from "app/lib/projector-settings-js/poster-size";
import { QUICKTAKE_CUSTOM_KEY, DEFAULT_ASPECT_RATIO } from "app/utils/Constant";

export default class BmmpAsset {
    constructor(private data) {}

    getId(): string {
        return this.data.id;
    }

    getTitle(): string | undefined {
        return this.data.title;
    }

    getDuration(): number {
        return parseInt(this.data.duration, 10);
    }

    getCreateDate(): string | null {
        return extractCreateDate(this.data.createdUnixUTC);
    }

    getShowName(): string {
        return this.data.showName || findShowNameInTags(this.getContentTags());
    }

    getNiCodes(): Array<{ code: string }> {
        return this.data.ni ? this.data.ni.split(",").map(code => ({ code })) : [];
    }

    getExcludedCountriesList(): string[] {
        return this.data.excludedInCountries || [];
    }

    getComscoreMetadata(): ComscoreMetadata {
        const { comscore = {} } = this.data;
        return new ComscoreMetadata({
            "ntStEp": comscore["nt_st_ep"],
            "nsStTy": comscore["ns_st_ty"],
            "c5": comscore["c5"],
            "bbPubD": comscore["bb_pub_d"]
        });
    }

    getAspectRatio(): string {
        return this.data?.aspectRatio ?? DEFAULT_ASPECT_RATIO;
    }

    getCaptions(): Array<Caption> {
        const { captions: url } = this.data;
        return url ? [new Caption({ url })] : [];
    }

    getStreams(): Array<VideoStream> {
        return this.data.streams.map((stream) => new VideoStream(stream));
    }

    getImages(): Array<{ url: string, width: number, height: number }> {
        return extractImages(this.data);
    }

    getContentCategory(): string | undefined {
        return this.data.contentCategory;
    }

    getKeywords(): Array<string> {
        return this.data.keywords || [];
    }

    getPeoples(): Array<{ code: string, description: string | undefined }> {
        const peopleCodes = this.data.peopleCodes;
        if (!peopleCodes) {
            return [];
        }

        const keys = Object.keys(peopleCodes);
        return keys.map(code => ({ code, description: peopleCodes[code] }));
    }

    getCedexisAppID(): number | undefined {
        return this.data.cedexisAppID;
    }

    getCompressedID(): string | undefined {
        return this.data.compressed_id;
    }

    getAdTag(): string | undefined {
        return this.data.ad_tag;
    }

    getPrerollAdUnit(): string | undefined {
        return this.data.preroll_ad_unit;
    }

    getRawNiData(): string | undefined {
        return this.data.ni;
    }

    getPosterTemplateImage(): string | null {
        const images = this.getImages();
        if (!images) {
            return null;
        }

        for (let i = 0; i < images.length; i++) {
            const { url } = images[i];
            if (PosterSize.isTemplateImage(url)) {
                return url;
            }
        }

        return null;
    }

    getContentTags(): Array<{ id: string, type: string }> {
        return (this.data.metadata || {}).contentTags;
    }

    getSites(): Array<string> {
        return (this.data.metadata || {}).sites;
    }

    getCanonicalUrl(): Array<string> {
        return (this.data.metadata || {}).canonicalUrl;
    }

    getAdCuePoints(): Array<number> {
        if (!this.data.adCuePoints) {
            return [];
        }

        return this.data.adCuePoints.split(",").map(item => parseFloat(item)).sort((a, b) => a - b);
    }

    getCustomAdKey(): string | null {
        return isQuicktakeVideo(this.getContentTags()) ? QUICKTAKE_CUSTOM_KEY : null;
    }
}

function isQuicktakeVideo(contentTags = []) {
    return contentTags.some(contentTag => contentTag.type === "Series" && (contentTag.id === "quicktake" || contentTag.id === "bloomberg-originals"));
}

function extractCreateDate(createdStr) {
    const createdunixUTCSeconds = parseInt(createdStr, 10);
    const createdUnixUTCMillis = createdunixUTCSeconds * 1000;
    const createdUnixUTCDate = new Date(createdUnixUTCMillis);

    return isNaN(createdUnixUTCDate.getTime()) ? null : createdUnixUTCDate.toISOString().slice(0, 10);
}

function extractImages({ thumbnail }) {
    const { images } = thumbnail;

    return images ? Object.keys(images).reduce((result, cropSize) => {
        const crop = images[cropSize];

        result.push({
            url: crop.url[0],
            width: parseInt(crop.width, 10),
            height: parseInt(crop.height, 10)
        });

        return result;
    }, []) : {};
}

const FILTERED_SERIES_ID = {
    "digital-originals": true,
    "tv-shows": true,
    "video-series": true
};

function findShowNameInTags(contentTags = []) {
    const series = contentTags.filter(({ type }) => type === "Series");
    const showSeries = series.length === 1
        ? series[0]
        : series.find(({ id }) => !FILTERED_SERIES_ID[id]);
    return showSeries?.id || "";
}
